/**
 * Lead capture utilities for Santa Mónica.
 * - getUTMs(): persisted UTM params across page navigations
 * - postLead(): POST /api/leads/<kind> — also fires fbq Lead/Schedule + sends
 *   meta_tracking (event_id, fbp, fbc, url) so the backend can later dedup
 *   with the Meta Conversions API.
 * - trackEvent(): fire-and-forget event log for our own backend analytics
 */
import { trackEvent as fbqTrack, getBrowserSignals } from './metaTrack';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const STORAGE_KEY = 'sm_utm';

export function captureUTMsFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    let any = false;
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) { found[k] = v; any = true; }
    });
    if (any) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(found));
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[utm] capture failed', e);
  }
}

export function getUTMs() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function splitName(full) {
  const parts = (full || '').trim().split(/\s+/);
  return {
    first_name: parts[0] || '',
    last_name:  parts.slice(1).join(' ') || '',
  };
}

// Primary conversion event for every lead form is "Lead" (deduped with the
// backend CAPI via the SAME event_id). The 'visit' form additionally fires
// a separate "Schedule" event with its OWN event_id — Meta requires that an
// event_id never be shared across different event_name values.
export async function postLead(kind, data) {
  const contentName =
    data.modelo_interes || data.modelo || data.lote || 'Santa Mónica Residencial';

  // 1) Primary "Lead" event — browser Pixel first, event_id travels to backend
  //    inside meta_tracking so the server-side CAPI can send Lead with the
  //    same event_id (dedup).
  const leadEventId = fbqTrack('Lead', {
    content_name:     contentName,
    content_category: kind,
  });
  const leadSignals = getBrowserSignals(leadEventId);

  // 2) For visit forms, fire a SEPARATE "Schedule" event (its own event_id B)
  //    both in the browser and server-side via /api/meta/event.
  if (kind === 'visit') {
    const scheduleEventId = fbqTrack('Schedule', {
      content_name:     contentName,
      content_category: 'visit',
    });
    const scheduleSignals = getBrowserSignals(scheduleEventId);
    // Fire-and-forget server-side Schedule. Keepalive survives navigation.
    try {
      fetch(`${API_BASE}/api/meta/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          event_name:       'Schedule',
          event_id:         scheduleSignals.event_id,
          event_source_url: scheduleSignals.event_source_url,
          fbp:              scheduleSignals.fbp,
          fbc:              scheduleSignals.fbc,
          custom_data: {
            content_name:     contentName,
            content_category: 'visit',
          },
        }),
      }).catch(() => { /* swallow */ });
    } catch { /* ignore */ }
  }

  try {
    const res = await fetch(`${API_BASE}/api/leads/${kind}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...data,
        utm: { ...getUTMs(), ...(data.utm || {}) },
        meta_tracking: leadSignals,
      }),
    });
    const json = await res.json();
    return { ok: res.ok, event_id: leadEventId, ...json };
  } catch (e) {
    console.error('[lead]', kind, e);
    return { ok: false, event_id: leadEventId, error: e.message };
  }
}

export function trackEvent(event, extra = {}) {
  try {
    fetch(`${API_BASE}/api/leads/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event,
        source_page: window.location.pathname + window.location.hash,
        utm: getUTMs(),
        ...extra,
      }),
      keepalive: true,
    }).catch((err) => {
      if (process.env.NODE_ENV !== 'production') console.warn('[trackEvent] fetch failed', err);
    });
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.warn('[trackEvent] failed', e);
  }
}
